import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X, Home, BookOpen, Star, Mail, ChevronDown, User, Layers, MonitorPlay, Sun, Moon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { LanguageToggle } from './LanguageToggle';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { effectiveTheme, toggleTheme } = useTheme();
  const isLightTheme = effectiveTheme === 'light';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', icon: Home, label: t('navigation.home') },
    { path: '/about', icon: User, label: t('navigation.about') },
    { path: '/solutions', icon: Layers, label: t('navigation.solutions') },
    { path: '/demo', icon: MonitorPlay, label: t('navigation.demo') },
    { path: '/blog', icon: BookOpen, label: t('navigation.blog') },
    { path: '/testimonials', icon: Star, label: t('navigation.testimonials') },
    { path: '/contact', icon: Mail, label: t('navigation.contact') },
  ];

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <motion.nav
        initial="hidden"
        animate="visible"
        variants={navVariants}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
          isScrolled ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-slate-200 dark:border-gray-700' : 'bg-white dark:bg-gray-900 border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex-shrink-0"
            >
              <Link to="/" className="text-slate-900 dark:text-gray-100 font-semibold text-xl tracking-tight">
                Victor Lazari
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 text-sm font-medium transition-colors duration-300 ${
                    location.pathname === item.path
                      ? 'text-sky-600 dark:text-cyan-400'
                      : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Theme Toggle & Language Toggle */}
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={isLightTheme ? 'Switch to dark theme' : 'Switch to light theme'}
                  aria-label="Toggle theme"
                >
                  {isLightTheme ? (
                    <Sun className="w-4 h-4 text-cyan-600" />
                  ) : (
                    <Moon className="w-4 h-4 text-cyan-400" />
                  )}
                  <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                    {isLightTheme ? 'Light' : 'Dark'}
                  </span>
                </button>
                <LanguageToggle variant="nav" />
              </div>
              {/* Mobile menu button */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-gray-100 transition-colors duration-300"
                >
                  {isOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-t border-slate-200 dark:border-gray-700"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors duration-300 ${
                      location.pathname === item.path
                        ? 'bg-sky-50 dark:bg-cyan-900/30 text-sky-700 dark:text-cyan-400'
                        : 'text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-800 hover:text-slate-900 dark:hover:text-gray-100'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleTheme}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-1 justify-center"
                    title={isLightTheme ? 'Switch to dark theme' : 'Switch to light theme'}
                    aria-label="Toggle theme"
                  >
                    {isLightTheme ? (
                      <Sun className="w-4 h-4 text-cyan-600" />
                    ) : (
                      <Moon className="w-4 h-4 text-cyan-400" />
                    )}
                    <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                      {isLightTheme ? 'Light' : 'Dark'}
                    </span>
                  </button>
                  <LanguageToggle variant="nav" className="flex-1 justify-center" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Overlay for mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 md:hidden"
          />
        )}
      </AnimatePresence>
    </>
  );
};